import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button } from '@hostfully/ui-components';
import {
  PublicFormButtonContainer,
  PublicFormInputContainer,
} from '@/pages/_layouts/publicLayout/publicForm.styles';
import FormFieldset from '@/components/forms/form/FormFieldset';
import PasswordField from '@/components/forms/PasswordField';
import { usePasswordStrength } from '@/hooks/usePasswordStrength';

interface ResetPasswordFormProps {
  clearErrors: () => void;
  errorMessage?: string;
  isSubmitting: boolean;
}

const ResetPasswordForm = ({
  clearErrors,
  errorMessage,
  isSubmitting,
}: ResetPasswordFormProps) => {
  const { t } = useTranslation();
  const { trigger, getValues } = useFormContext();
  const { password, confirmPassword } = useWatch();
  const { setPasswordStrength } = usePasswordStrength();

  useEffect(() => {
    clearErrors();
  }, [clearErrors, password, confirmPassword]);

  useEffect(() => {
    if (getValues('confirmPassword')) {
      trigger('confirmPassword');
    }
  }, [password, trigger, getValues]);

  return (
    <FormFieldset disabled={isSubmitting}>
      <PublicFormInputContainer>
        <PasswordField
          label={t('pageForgotPassword.newPassword')}
          name="password"
          placeholder={t('pageForgotPassword.newPassword')}
          enableStrengthCheck
          onStrengthChange={setPasswordStrength}
        />
      </PublicFormInputContainer>
      <PublicFormInputContainer>
        <PasswordField
          label={t('pageForgotPassword.confirmPassword')}
          name="confirmPassword"
          placeholder={t('pageForgotPassword.confirmPassword')}
        />
      </PublicFormInputContainer>
      <PublicFormButtonContainer>
        <Button
          data-testid="submit-button"
          disabled={!!errorMessage || isSubmitting}
          size="large"
          type="submit"
        >
          {t('pageForgotPassword.confirm')}
        </Button>
      </PublicFormButtonContainer>
      {!!errorMessage && (
        <p className="error-message" data-testid="error-message">
          {errorMessage}
        </p>
      )}
    </FormFieldset>
  );
};

export default ResetPasswordForm;
